import type { CarouselVariant } from "./contracts.js";

export type ExportFormat = "meta-vertical" | "meta-feed" | "square" | "landscape";

export type ContentSafeArea = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

type CarouselLayout = {
  cardWidth: number;
  cardHeight: number;
  gap: number;
  inactiveScale: number;
  showHeading?: boolean;
  curveDepth?: number;
  curveTilt?: number;
};

export type ExportFormatDefinition = {
  id: ExportFormat;
  label: string;
  aspectLabel: string;
  width: number;
  height: number;
  fps: 30;
  orientation: "portrait" | "square" | "landscape";
  presentation: "combined" | "full-width";
  filenameSuffix: string;
  safeArea: ContentSafeArea;
  carousel: Record<CarouselVariant, CarouselLayout>;
};

export const DEFAULT_EXPORT_FORMAT: ExportFormat = "meta-vertical";

export const EXPORT_FORMATS: Record<ExportFormat, ExportFormatDefinition> = {
  "meta-vertical": {
    id: "meta-vertical",
    label: "Meta Reels & Stories",
    aspectLabel: "9:16",
    width: 1080,
    height: 1920,
    fps: 30,
    orientation: "portrait",
    presentation: "combined",
    filenameSuffix: "meta-reels-1080x1920",
    safeArea: { top: .115, right: .06, bottom: .165, left: .06 },
    carousel: {
      "focus-carousel": { cardWidth: 82, cardHeight: 70, gap: 4, inactiveScale: .72, showHeading: false },
      "vertical-focus-carousel": { cardWidth: 82, cardHeight: 66, gap: 6, inactiveScale: .7, showHeading: false },
      "curved-focus-carousel": { cardWidth: 82, cardHeight: 70, gap: .5, inactiveScale: .76, showHeading: false, curveDepth: 4, curveTilt: 4.5 },
    },
  },
  "meta-feed": {
    id: "meta-feed",
    label: "Meta Feed",
    aspectLabel: "4:5",
    width: 1080,
    height: 1350,
    fps: 30,
    orientation: "portrait",
    presentation: "combined",
    filenameSuffix: "meta-feed-1080x1350",
    safeArea: { top: .07, right: .055, bottom: .08, left: .055 },
    carousel: {
      "focus-carousel": { cardWidth: 54, cardHeight: 60, gap: 5, inactiveScale: .73 },
      "vertical-focus-carousel": { cardWidth: 56, cardHeight: 58, gap: 7, inactiveScale: .7 },
      "curved-focus-carousel": { cardWidth: 55, cardHeight: 64, gap: 1.5, inactiveScale: .78, curveDepth: 5.5, curveTilt: 6 },
    },
  },
  square: {
    id: "square",
    label: "Square",
    aspectLabel: "1:1",
    width: 1080,
    height: 1080,
    fps: 30,
    orientation: "square",
    presentation: "combined",
    filenameSuffix: "square-1080x1080",
    safeArea: { top: .06, right: .05, bottom: .06, left: .05 },
    carousel: {
      "focus-carousel": { cardWidth: 43, cardHeight: 62, gap: 4, inactiveScale: .74 },
      "vertical-focus-carousel": { cardWidth: 45, cardHeight: 62, gap: 7, inactiveScale: .7 },
      "curved-focus-carousel": { cardWidth: 44, cardHeight: 66, gap: 2, inactiveScale: .8, curveDepth: 6, curveTilt: 6 },
    },
  },
  landscape: {
    id: "landscape",
    label: "Landscape",
    aspectLabel: "16:9",
    width: 1920,
    height: 1080,
    fps: 30,
    orientation: "landscape",
    presentation: "full-width",
    filenameSuffix: "landscape-1920x1080",
    safeArea: { top: .048, right: .045, bottom: .05, left: .045 },
    carousel: {
      "focus-carousel": { cardWidth: 29, cardHeight: 62, gap: 4, inactiveScale: .76 },
      "vertical-focus-carousel": { cardWidth: 29, cardHeight: 68, gap: 7, inactiveScale: .76 },
      "curved-focus-carousel": { cardWidth: 29, cardHeight: 70, gap: 2, inactiveScale: .82, curveDepth: 7, curveTilt: 7 },
    },
  },
};

export const EXPORT_FORMAT_OPTIONS = Object.values(EXPORT_FORMATS);

export function isExportFormat(value: unknown): value is ExportFormat {
  return typeof value === "string" && value in EXPORT_FORMATS;
}

export function getExportFormat(format: ExportFormat): ExportFormatDefinition {
  return EXPORT_FORMATS[format];
}

export function exportFilename(format: ExportFormat): string {
  return `${EXPORT_FORMATS[format].filenameSuffix}.mp4`;
}
